import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import translate from '../../libs/i18n';
import getQuantitiveStatus from '../../utils/getQuantitiveStatus';
import roundToPrecision from '../../utils/roundToPrecision';
import toTitleCase from '../../utils/toTitleCase';
import { reportDefaultProps } from '../../utils/defaultProps';
import { reportShape } from '../../utils/propTypesShapes';

import { QuantitativeAnalysisStyle } from './QuantitativeAnalysisStyle';

const QuantitativeAnalysis = ({ report }) => {
  const language = useSelector((state) => state.settings.language);

  const getQuantitiveData = (value, total, type) => {
    const data = {
      value: 0,
      percent: 0,
      status: getQuantitiveStatus(0, type),
    };

    if (value) {
      data.value = value;
    }

    if (total) {
      data.percent = value / total;
    }

    if (data.percent) {
      data.status = getQuantitiveStatus(0, type);
    }

    return data;
  };

  const quantitiveAnalysis = useMemo(() => {
    if (report) {
      const currentQuantitiveAnalysis = {
        tlv: report.tlv ? roundToPrecision(report.tlv, 1) : 0,
        tlw: report.tlw ? roundToPrecision(report.tlw, 1) : 0,
        pulmonaryInvolvementExtent: getQuantitiveData(
          report.pulmonary_involvement_extent_tlv || 0,
          100,
          'volume'
        ),
        pulmonaryInvolvementSeverity: getQuantitiveData(
          report.pulmonary_involvement_extent_tlv || 0,
          100,
          'weight'
        ),
        low: {
          ml: getQuantitiveData(report.low_ml || 0, report.tlv || 0, 'volume'),
          g: getQuantitiveData(report.low_g || 0, report.tlw || 0, 'weight'),
        },
        normal: {
          ml: getQuantitiveData(
            report.normal_ml || 0,
            report.tlv || 0,
            'volume'
          ),
          g: getQuantitiveData(report.normal_g || 0, report.tlw || 0, 'weight'),
        },
        high: {
          ml: getQuantitiveData(report.high_ml || 0, report.tlv || 0, 'volume'),
          g: getQuantitiveData(report.high_g || 0, report.tlw || 0, 'weight'),
        },
      };

      return currentQuantitiveAnalysis;
    }

    return null;
  }, [report]);

  if (!quantitiveAnalysis) {
    return null;
  }

  const {
    tlv,
    tlw,
    pulmonaryInvolvementExtent,
    pulmonaryInvolvementSeverity,
    low,
    normal,
    high,
  } = quantitiveAnalysis;

  return (
    <QuantitativeAnalysisStyle>
      <div className="table-head">
        <h3 className="head-title">
          {translate('quantitiveAnalysis', language)}
        </h3>
        <div className="main-data">
          <div className="main-data-item">
            <p className="item-label">TLV (mL)</p>
            <p className="item-value">{tlv}</p>
          </div>
          <div className="main-data-item">
            <p className="item-label">TLW (g)</p>
            <p className="item-value">{tlw}</p>
          </div>
          <div className="main-data-item">
            <p className="item-label">{`${toTitleCase(
              translate('density', language)
            )} (g/mL)`}</p>
            <p className="item-value">{`${
              tlv === 0 ? 0 : roundToPrecision(tlw / tlv, 1)
            }`}</p>
          </div>
        </div>
      </div>
      <div className="general-data">
        {pulmonaryInvolvementExtent ? (
          <div className="general-data-item theme-dark">
            <p className="item-label">
              {`${translate('pulmonaryInvolvementExtent', language)} `}
              <span className="label-unit">(%TLV)</span>
            </p>
            <p className="item-value">
              {`${roundToPrecision(pulmonaryInvolvementExtent.value, 1)}%`}
            </p>
            <p
              className={`item-status status-${pulmonaryInvolvementExtent.status}`}
            >
              {translate(pulmonaryInvolvementExtent.status, language)}
            </p>
          </div>
        ) : null}
        {pulmonaryInvolvementSeverity ? (
          <div className="general-data-item theme-dark">
            <p className="item-label">
              {`${translate('pulmonaryInvolvementSeverity', language)} `}
              <span className="label-unit">(%TLW)</span>
            </p>
            <p className="item-value">{`${roundToPrecision(
              pulmonaryInvolvementSeverity.value,
              2
            )}%`}</p>
            <p
              className={`item-status status-${pulmonaryInvolvementSeverity.status}`}
            >
              {translate(pulmonaryInvolvementSeverity.status, language)}
            </p>
          </div>
        ) : null}
        {low ? (
          <div className="general-data-item theme-light">
            <p className="item-label">
              {`${toTitleCase(translate('low', language))} `}
              <span className="label-unit">{`(${`mL ${translate(
                'and',
                language
              )} %TLV`})`}</span>
            </p>
            <p className="item-value">{`${roundToPrecision(
              low.ml.value,
              2
            )} mL | ${roundToPrecision(low.ml.percent * 100, 1)}%`}</p>
            <p className={`item-status status-${low.ml.status}`}>
              {translate(low.ml.status, language)}
            </p>
          </div>
        ) : null}
        {normal ? (
          <div className="general-data-item theme-light">
            <p className="item-label">
              {'Normal '}
              <span className="label-unit">{`(${`mL ${translate(
                'and',
                language
              )} %TLV`})`}</span>
            </p>
            <p className="item-value">{`${roundToPrecision(
              normal.ml.value,
              2
            )} mL | ${roundToPrecision(normal.ml.percent * 100, 1)}%`}</p>
            <p className={`item-status status-${normal.ml.status}`}>
              {translate(normal.ml.status, language)}
            </p>
          </div>
        ) : null}
        {high ? (
          <div className="general-data-item theme-light">
            <p className="item-label">
              {`${toTitleCase(translate('high', language))} `}
              <span className="label-unit">{`(${`mL ${translate(
                'and',
                language
              )} %TLV`})`}</span>
            </p>
            <p className="item-value">{`${roundToPrecision(
              high.ml.value,
              2
            )} mL | ${roundToPrecision(high.ml.percent * 100, 1)}%`}</p>
            <p className={`item-status status-${high.ml.status}`}>
              {translate(high.ml.status, language)}
            </p>
          </div>
        ) : null}
        {low ? (
          <div className="general-data-item theme-dark">
            <p className="item-label">
              {`${toTitleCase(translate('low', language))} `}
              <span className="label-unit">{`(${`g ${translate(
                'and',
                language
              )} %TLW`})`}</span>
            </p>
            <p className="item-value">{`${roundToPrecision(
              low.g.value,
              2
            )} g | ${roundToPrecision(low.g.percent * 100, 1)}%`}</p>
            <p className={`item-status status-${low.g.status}`}>
              {translate(low.g.status, language)}
            </p>
          </div>
        ) : null}
        {normal ? (
          <div className="general-data-item theme-dark">
            <p className="item-label">
              {'Normal '}
              <span className="label-unit">{`(${`g ${translate(
                'and',
                language
              )} %TLW`})`}</span>
            </p>
            <p className="item-value">{`${roundToPrecision(
              normal.g.value,
              2
            )} g | ${roundToPrecision(normal.g.percent * 100, 1)}%`}</p>
            <p className={`item-status status-${normal.g.status}`}>
              {translate(normal.g.status, language)}
            </p>
          </div>
        ) : null}
        {high ? (
          <div className="general-data-item theme-dark">
            <p className="item-label">
            {`${toTitleCase(translate('high', language))} `}
              <span className="label-unit">{`(${`g ${translate(
                'and',
                language
              )} %TLW`})`}</span>
            </p>
            <p className="item-value">{`${roundToPrecision(
              high.g.value,
              2
            )} g | ${roundToPrecision(high.g.percent * 100, 1)}%`}</p>
            <p className={`item-status status-${high.g.status}`}>
              {translate(high.g.status, language)}
            </p>
          </div>
        ) : null}
      </div>
    </QuantitativeAnalysisStyle>
  );
};

QuantitativeAnalysis.propTypes = {
  report: reportShape,
};

QuantitativeAnalysis.defaultProps = {
  report: reportDefaultProps,
};

export default QuantitativeAnalysis;
